import firebase from 'firebase/app';
import EventEmitter from 'eventemitter3';
import uuidv4 from 'uuid/v4';
import 'firebase/auth';
import 'firebase/firestore';

/*! *****************************************************************************
Copyright (c) Microsoft Corporation. All rights reserved.
Licensed under the Apache License, Version 2.0 (the "License"); you may not use
this file except in compliance with the License. You may obtain a copy of the
License at http://www.apache.org/licenses/LICENSE-2.0

THIS CODE IS PROVIDED ON AN *AS IS* BASIS, WITHOUT WARRANTIES OR CONDITIONS OF ANY
KIND, EITHER EXPRESS OR IMPLIED, INCLUDING WITHOUT LIMITATION ANY IMPLIED
WARRANTIES OR CONDITIONS OF TITLE, FITNESS FOR A PARTICULAR PURPOSE,
MERCHANTABLITY OR NON-INFRINGEMENT.

See the Apache Version 2.0 License for specific language governing permissions
and limitations under the License.
***************************************************************************** */
/* global Reflect, Promise */

var extendStatics = Object.setPrototypeOf ||
    ({ __proto__: [] } instanceof Array && function (d, b) { d.__proto__ = b; }) ||
    function (d, b) { for (var p in b) if (b.hasOwnProperty(p)) d[p] = b[p]; };

function __extends(d, b) {
    extendStatics(d, b);
    function __() { this.constructor = d; }
    d.prototype = b === null ? Object.create(b) : (__.prototype = b.prototype, new __());
}

var Timestamp = (function () {
    function Timestamp(seconds, nanoseconds, serverTimestamp) {
        this.seconds = seconds;
        this.nanoseconds = nanoseconds;
        this.serverTimestamp = serverTimestamp || false;
        this.toDate = this.toDate.bind(this);
        this.toMillis = this.toMillis.bind(this);
    }
    Timestamp.fromDate = function (date) {
        return Timestamp.fromMillis(date.getTime());
    };
    Timestamp.now = function () {
        return Timestamp.fromMillis(Date.now());
    };
    Timestamp.serverTimestamp = function () {
        var now = this.now();
        return new Timestamp(now.seconds, now.nanoseconds, true);
    };
    Timestamp.fromMillis = function (millis) {
        return new Timestamp(Math.trunc(millis / 1000), (millis % 1000) * 1000000);
    };
    Timestamp.of = function (seconds, nanoseconds) {
        return new Timestamp(seconds, nanoseconds);
    };
    Timestamp.prototype.toDate = function () {
        return new Date(this.seconds * 1000 + this.nanoseconds / 1000000);
    };
    Timestamp.prototype.toMillis = function () {
        return this.seconds * 1000 + this.nanoseconds / 1000000;
    };
    Timestamp.prototype.isBefore = function (other) {
        return this.compareTo(other) < 0;
    };
    Timestamp.prototype.isAfter = function (other) {
        return this.compareTo(other) > 0;
    };
    Timestamp.prototype.compareTo = function (other) {
        if (this.seconds < other.seconds) {
            return -1;
        }
        if (this.seconds > other.seconds) {
            return 1;
        }
        if (this.nanoseconds < other.nanoseconds) {
            return -1;
        }
        if (this.nanoseconds > other.nanoseconds) {
            return 1;
        }
        return 0;
    };
    Timestamp.EPOCH = new Timestamp(0, 0);
    return Timestamp;
}());

function fromFirebaseTimestamp(timestamp) {
    return timestamp && Timestamp.of(timestamp.seconds, timestamp.nanoseconds);
}
function toFirebaseTimestamp(timestamp) {
    if (timestamp) {
        return timestamp.serverTimestamp
            ? firebase.firestore.FieldValue.serverTimestamp()
            : new firebase.firestore.Timestamp(timestamp.seconds, timestamp.nanoseconds);
    }
}

var BaseModel = (function () {
    function BaseModel() {
        this._eventEmitter = new EventEmitter();
    }
    BaseModel.prototype.on = function (event, fn, context) {
        this._eventEmitter.on(event, fn, context);
        return this;
    };
    BaseModel.prototype.once = function (event, fn, context) {
        this._eventEmitter.once(event, fn, context);
        return this;
    };
    BaseModel.prototype.off = function (event, fn, context, once) {
        var _this = this;
        var events = (event && [event]) || this._eventEmitter.eventNames();
        events.forEach(function (oneEvent) {
            if (fn || (!context && !once)) {
                _this._eventEmitter.off(oneEvent, fn, context, once);
            }
            else {
                _this._eventEmitter.listeners(oneEvent).forEach(function (listener) {
                    _this._eventEmitter.off(oneEvent, listener, context, once);
                });
            }
        });
        return this;
    };
    BaseModel.prototype.close = function () {
        this._eventEmitter.removeAllListeners();
    };
    return BaseModel;
}());

var FirebaseMessageChannel;
(function (FirebaseMessageChannel) {
    FirebaseMessageChannel["SMS"] = "SMS";
    FirebaseMessageChannel["EMAIL"] = "EMAIL";
    FirebaseMessageChannel["FACEBOOK"] = "FACEBOOK";
})(FirebaseMessageChannel || (FirebaseMessageChannel = {}));
var FirebaseMessageReceiptType;
(function (FirebaseMessageReceiptType) {
    FirebaseMessageReceiptType[FirebaseMessageReceiptType["PENDING"] = 0] = "PENDING";
    FirebaseMessageReceiptType[FirebaseMessageReceiptType["SENT"] = 1] = "SENT";
    FirebaseMessageReceiptType[FirebaseMessageReceiptType["DELIVERED"] = 2] = "DELIVERED";
    FirebaseMessageReceiptType[FirebaseMessageReceiptType["READ"] = 3] = "READ";
})(FirebaseMessageReceiptType || (FirebaseMessageReceiptType = {}));
var FirebaseMessage = (function (_super) {
    __extends(FirebaseMessage, _super);
    function FirebaseMessage(client, conversation, messageId) {
        var _this = _super.call(this) || this;
        _this._client = client;
        _this._conversation = conversation;
        _this._messageId = messageId;
        _this._sentBy = _this._client.myself;
        _this._parts = [];
        _this._metadata = {};
        _this._receipts = {};
        _this._draft = false;
        _this._sentAt = Timestamp.serverTimestamp();
        _this._pending = false;
        _this._initialized = false;
        _this._realSender = _this._client.myself;
        _this.init = _this.init.bind(_this);
        _this.initDraft = _this.initDraft.bind(_this);
        _this.finalizeDraft = _this.finalizeDraft.bind(_this);
        _this.updateMessage = _this.updateMessage.bind(_this);
        _this.handleReadUntilChange = _this.handleReadUntilChange.bind(_this);
        _this.handleDeliveredUntilChange = _this.handleDeliveredUntilChange.bind(_this);
        _this.addDraftPart = _this.addDraftPart.bind(_this);
        return _this;
    }
    Object.defineProperty(FirebaseMessage.prototype, "conversation", {
        get: function () {
            return this._conversation;
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(FirebaseMessage.prototype, "messageId", {
        get: function () {
            return this._messageId;
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(FirebaseMessage.prototype, "sentAt", {
        get: function () {
            return this._sentAt;
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(FirebaseMessage.prototype, "sentBy", {
        get: function () {
            return this._sentBy;
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(FirebaseMessage.prototype, "parts", {
        get: function () {
            return this._parts;
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(FirebaseMessage.prototype, "channels", {
        get: function () {
            return this._channels;
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(FirebaseMessage.prototype, "skipChannels", {
        get: function () {
            return this._skipChannels;
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(FirebaseMessage.prototype, "sentVia", {
        get: function () {
            return this._sentVia;
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(FirebaseMessage.prototype, "metadata", {
        get: function () {
            return this._metadata;
        },
        set: function (data) {
            if (!this._draft) {
                throw new Error('Changing metadata not allowed in non-draft message');
            }
            this._metadata = Object.assign({}, data);
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(FirebaseMessage.prototype, "pending", {
        get: function () {
            return this._pending;
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(FirebaseMessage.prototype, "initialized", {
        get: function () {
            return this._initialized;
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(FirebaseMessage.prototype, "draft", {
        get: function () {
            return this._draft;
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(FirebaseMessage.prototype, "receipts", {
        get: function () {
            var _this = this;
            var receipts = {};
            this._conversation.participants.forEach(function (participant) {
                if (_this._pending) {
                    receipts[participant.userId] = FirebaseMessageReceiptType.PENDING;
                }
                else {
                    receipts[participant.userId] =
                        _this._receipts[participant.userId] || FirebaseMessageReceiptType.SENT;
                }
            });
            return receipts;
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(FirebaseMessage.prototype, "realSender", {
        get: function () {
            return this._realSender;
        },
        enumerable: true,
        configurable: true
    });
    FirebaseMessage.prototype.initDraft = function () {
        if (this._initialized) {
            throw new Error('Already initialized!');
        }
        this._initialized = true;
        this._draft = true;
        this._sentBy = this._client.myself;
        this._receipts = {};
        this._eventEmitter.emit('initialized', {
            event: 'initialized',
            message: this
        });
        return this;
    };
    FirebaseMessage.prototype.finalizeDraft = function () {
        this._draft = false;
        this._pending = true;
        this._receipts[this._sentBy.userId] = FirebaseMessageReceiptType.READ;
        Object.freeze(this._parts);
        Object.freeze(this._metadata);
        Object.freeze(this._channels);
        Object.freeze(this._skipChannels);
    };
    FirebaseMessage.prototype.createPartFromFirestoreData = function (part) {
        return new FirebaseMessagePart(part.type, JSON.parse(part.body), this);
    };
    FirebaseMessage.prototype.addDraftPart = function (type, body) {
        if (!this._draft) {
            throw new Error('Message is not a draft, unable to create draft parts');
        }
        var part = new FirebaseMessagePart(type, body, this);
        this._parts.push(part);
        return part;
    };
    FirebaseMessage.prototype.initMessage = function (message, deliveryTimestamps, readTimestamps, sentBy, realSender) {
        var _this = this;
        var messageDoc = message.data({ serverTimestamps: 'estimate' });
        this._sentAt = fromFirebaseTimestamp(messageDoc.sentAt) || Timestamp.now();
        this._sentBy = sentBy;
        this._realSender = realSender;
        this._metadata = messageDoc.metadata;
        this._channels = messageDoc.channels;
        this._skipChannels = messageDoc.skipChannels;
        this._sentVia = messageDoc.sentVia;
        this._parts = messageDoc.parts.map(function (part) {
            return _this.createPartFromFirestoreData(part);
        });
        this._pending = message.metadata.hasPendingWrites;
        this._receipts = {};
        this._receipts[messageDoc.sentBy] = FirebaseMessageReceiptType.READ;
        deliveryTimestamps.forEach(function (timestamp) { return _this.updateDeliveryReceipt(timestamp); });
        readTimestamps.forEach(function (timestamp) { return _this.updateReadReceipt(timestamp); });
        Object.freeze(this._parts);
        Object.freeze(this._metadata);
        Object.freeze(this._channels);
        Object.freeze(this._skipChannels);
        this._initialized = true;
        this._eventEmitter.emit('initialized', {
            event: 'initialized',
            message: this
        });
        return this;
    };
    FirebaseMessage.prototype.init = function (message, deliveryTimestamps, readTimestamps) {
        var _this = this;
        if (message.id !== this._messageId) {
            throw new Error("Expect message " + this._messageId + ", but found " + message.id);
        }
        if (this._initialized) {
            return Promise.resolve(this);
        }
        var messageDoc = message.data({ serverTimestamps: 'estimate' });
        if (messageDoc.realSenderId) {
            return this._client.getUsers([messageDoc.sentBy, messageDoc.realSenderId]).then(function (users) {
                var userMap = new Map();
                users.forEach(function (user) { return userMap.set(user.userId, user); });
                var realSender = userMap.get(messageDoc.realSenderId);
                var sentBy = userMap.get(messageDoc.sentBy);
                return _this.initMessage(message, deliveryTimestamps, readTimestamps, sentBy, realSender);
            });
        }
        else {
            return this._client
                .getUsers([messageDoc.sentBy])
                .then(function (_a) {
                var sentBy = _a[0];
                return _this.initMessage(message, deliveryTimestamps, readTimestamps, sentBy, sentBy);
            });
        }
    };
    FirebaseMessage.prototype.updateMessage = function (message) {
        this._metadata = message.get('metadata');
        Object.freeze(this._metadata);
        if (this._pending && !message.metadata.hasPendingWrites) {
            this._pending = false;
            this._sentAt = fromFirebaseTimestamp(message.get('sentAt')) || Timestamp.now();
            if (this._initialized) {
                this._eventEmitter.emit('sent', {
                    event: 'sent',
                    message: this
                });
            }
        }
        else {
            this._eventEmitter.emit('changed', {
                event: 'changed',
                message: this
            });
        }
    };
    Object.defineProperty(FirebaseMessage.prototype, "read", {
        get: function () {
            return this._receipts[this._client.myself.userId] === FirebaseMessageReceiptType.READ;
        },
        enumerable: true,
        configurable: true
    });
    FirebaseMessage.prototype.updateDeliveryReceipt = function (doc) {
        var timestamp = fromFirebaseTimestamp(doc.get('timestamp')) || Timestamp.EPOCH;
        var userId = doc.get('userId');
        if (!timestamp.isBefore(this._sentAt) &&
            this._receipts[userId] !== FirebaseMessageReceiptType.READ &&
            this._receipts[userId] !== FirebaseMessageReceiptType.DELIVERED) {
            this._receipts[userId] = FirebaseMessageReceiptType.DELIVERED;
            if (this._initialized && this._client.myself.userId !== userId) {
                this._eventEmitter.emit('delivered', {
                    event: 'delivered',
                    message: this,
                    userId: userId
                });
            }
        }
    };
    FirebaseMessage.prototype.handleDeliveredUntilChange = function (change) {
        switch (change.type) {
            case 'removed': {
                delete this._receipts[change.doc.id];
                break;
            }
            case 'added':
            case 'modified': {
                this.updateDeliveryReceipt(change.doc);
                break;
            }
        }
    };
    FirebaseMessage.prototype.updateReadReceipt = function (doc) {
        var timestamp = fromFirebaseTimestamp(doc.get('timestamp')) || Timestamp.EPOCH;
        var userId = doc.get('userId');
        if (!timestamp.isBefore(this._sentAt) &&
            this._receipts[userId] !== FirebaseMessageReceiptType.READ) {
            this._receipts[userId] = FirebaseMessageReceiptType.READ;
            if (this._initialized && this._client.myself.userId !== userId) {
                this._eventEmitter.emit('read', {
                    event: 'read',
                    message: this,
                    userId: userId
                });
            }
        }
    };
    FirebaseMessage.prototype.handleReadUntilChange = function (change) {
        switch (change.type) {
            case 'removed': {
                delete this._receipts[change.doc.id];
                break;
            }
            case 'added':
            case 'modified': {
                this.updateReadReceipt(change.doc);
                break;
            }
        }
    };
    return FirebaseMessage;
}(BaseModel));
var FirebaseMessagePart = (function () {
    function FirebaseMessagePart(type, body, message) {
        this.type = type;
        this.body = body;
        this.message = message;
    }
    FirebaseMessagePart.prototype.toFirestoreData = function () {
        return {
            type: this.type,
            body: JSON.stringify(this.body)
        };
    };
    return FirebaseMessagePart;
}());

var FirebaseMessageQuery = (function (_super) {
    __extends(FirebaseMessageQuery, _super);
    function FirebaseMessageQuery(store, conversation, client, limit) {
        var _this = _super.call(this) || this;
        _this._store = store;
        _this._conversation = conversation;
        _this._client = client;
        _this._existingMessages = [];
        _this._newMessages = [];
        _this._initialized = false;
        _this._hasMore = false;
        _this._pendingFetchMorePromises = [];
        _this.close = _this.close.bind(_this);
        _this.more = _this.more.bind(_this);
        _this._store
            .collection('conversations')
            .doc(_this._conversation.conversationId)
            .collection('messages')
            .orderBy('sentAt', 'desc')
            .limit(limit)
            .get()
            .then(function (snapshot) {
            Promise.all(snapshot.docs.map(function (doc) { return _this._conversation.getMessageInternal(doc); })).then(function (existingMessages) {
                _this._hasMore = snapshot.size === limit;
                _this._existingMessages = existingMessages;
                _this.recomputeList();
                var query = _this._store
                    .collection('conversations')
                    .doc(_this._conversation.conversationId)
                    .collection('messages')
                    .orderBy('sentAt', 'desc');
                if (!snapshot.empty && snapshot.docs.length > 0) {
                    var lastSentAt = snapshot.docs[0].get('sentAt') || toFirebaseTimestamp(Timestamp.now());
                    query = query.where('sentAt', '>', lastSentAt);
                    var deliveredUntil = _this._conversation.myDeliveryTimestamp;
                    if (!deliveredUntil || deliveredUntil.isBefore(fromFirebaseTimestamp(lastSentAt))) {
                        _this._conversation.markConversationDelivered(snapshot.docs[0].id);
                    }
                }
                _this._unsubscribeNewMessages = query.onSnapshot(function (newMessagesSnapshot) {
                    Promise.all(newMessagesSnapshot.docs.map(function (doc) { return _this._conversation.getMessageInternal(doc); })).then(function (newMessages) {
                        _this._newMessages = newMessages;
                        _this.recomputeList();
                    });
                }, function (error) {
                    return console.error("Conversation " + _this._conversation.conversationId + " Query New Message Subscription Empty Error (me=" + (_this._client.myself &&
                        _this._client.myself.userId) + "): ", error);
                });
                _this._initialized = true;
                _this._eventEmitter.emit('initialized', {
                    event: 'initialized',
                    query: _this
                });
            });
        });
        return _this;
    }
    Object.defineProperty(FirebaseMessageQuery.prototype, "initialized", {
        get: function () {
            return this._initialized;
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(FirebaseMessageQuery.prototype, "hasMore", {
        get: function () {
            return this._hasMore;
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(FirebaseMessageQuery.prototype, "messages", {
        get: function () {
            return this._newMessages.concat(this._existingMessages);
        },
        enumerable: true,
        configurable: true
    });
    FirebaseMessageQuery.prototype.recomputeList = function () {
        if (this._initialized) {
            this._eventEmitter.emit('updated', {
                event: 'updated',
                query: this
            });
        }
    };
    FirebaseMessageQuery.prototype.more = function (limit) {
        var _this = this;
        var promise;
        if (this._pendingFetchMorePromises.length > 0) {
            promise = this._pendingFetchMorePromises[this._pendingFetchMorePromises.length - 1].then(function () {
                return _this.fetchMore(limit);
            });
        }
        else {
            promise = this.fetchMore(limit);
        }
        this._pendingFetchMorePromises.push(promise);
        return promise.then(function (v) {
            _this._pendingFetchMorePromises.shift();
            return v;
        }, function (e) {
            _this._pendingFetchMorePromises.shift();
            throw e;
        });
    };
    FirebaseMessageQuery.prototype.fetchMore = function (limit) {
        var _this = this;
        if (this._hasMore && this._existingMessages.length !== 0) {
            return this._store
                .collection('conversations')
                .doc(this._conversation.conversationId)
                .collection('messages')
                .orderBy('sentAt', 'desc')
                .where('sentAt', '<', toFirebaseTimestamp(this._existingMessages[this._existingMessages.length - 1].sentAt))
                .limit(limit)
                .get()
                .then(function (snapshot) {
                _this._hasMore = snapshot.size === limit;
                if (!snapshot.empty) {
                    return Promise.all(snapshot.docs.map(function (doc) { return _this._conversation.getMessageInternal(doc); })).then(function (moreMessages) {
                        _this._eventEmitter.emit('fetchedMore', {
                            event: 'fetchedMore',
                            query: _this,
                            messages: moreMessages
                        });
                        _this._existingMessages = _this._existingMessages.concat(moreMessages);
                        _this.recomputeList();
                        return _this;
                    });
                }
                return _this;
            });
        }
        return Promise.resolve(this);
    };
    FirebaseMessageQuery.prototype.close = function () {
        _super.prototype.close.call(this);
        if (this._unsubscribeNewMessages) {
            this._unsubscribeNewMessages();
            this._unsubscribeNewMessages = undefined;
        }
    };
    return FirebaseMessageQuery;
}(BaseModel));

var FirebaseConversation = (function (_super) {
    __extends(FirebaseConversation, _super);
    function FirebaseConversation(store, client, conversationId) {
        var _this = _super.call(this) || this;
        _this._messageIdsToBeMarkedAsRead = [];
        _this._markConversationAsReadTimeout = undefined;
        _this._conversationId = conversationId;
        _this._createdAt = Timestamp.now();
        _this._updatedAt = Timestamp.now();
        _this._participants = [];
        _this._participantIds = new Set();
        _this._unreadCount = 0;
        _this._readOnly = true;
        _this._metadata = {};
        _this._store = store;
        _this._client = client;
        _this._messages = new Map();
        _this._typingTimeouts = new Map();
        _this._readTimestamps = [];
        _this._deliveryTimestamps = [];
        _this._initialized = false;
        _this.init = _this.init.bind(_this);
        _this.updateConversation = _this.updateConversation.bind(_this);
        _this.handleDeliveredUntilChange = _this.handleDeliveredUntilChange.bind(_this);
        _this.handleReadUntilChange = _this.handleReadUntilChange.bind(_this);
        _this.handleTypingUntilChange = _this.handleTypingUntilChange.bind(_this);
        _this.getMessage = _this.getMessage.bind(_this);
        _this.sendMessage = _this.sendMessage.bind(_this);
        _this.startTyping = _this.startTyping.bind(_this);
        _this.stopTyping = _this.stopTyping.bind(_this);
        _this.close = _this.close.bind(_this);
        _this.markConversationRead = _this.markConversationRead.bind(_this);
        _this.markConversationDelivered = _this.markConversationDelivered.bind(_this);
        _this.createDraftMessage = _this.createDraftMessage.bind(_this);
        _this.handleNewMessage = _this.handleNewMessage.bind(_this);
        return _this;
    }
    Object.defineProperty(FirebaseConversation.prototype, "_me", {
        get: function () {
            return this._client.myself;
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(FirebaseConversation.prototype, "conversationId", {
        get: function () {
            return this._conversationId;
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(FirebaseConversation.prototype, "createdAt", {
        get: function () {
            return this._createdAt;
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(FirebaseConversation.prototype, "updatedAt", {
        get: function () {
            return this._updatedAt;
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(FirebaseConversation.prototype, "lastMessage", {
        get: function () {
            return this._lastMessage;
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(FirebaseConversation.prototype, "participants", {
        get: function () {
            return this._participants.slice(0);
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(FirebaseConversation.prototype, "isParticipant", {
        get: function () {
            return this._participantIds.has(this._me.userId);
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(FirebaseConversation.prototype, "unreadCount", {
        get: function () {
            return this._unreadCount;
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(FirebaseConversation.prototype, "readOnly", {
        get: function () {
            return this._readOnly || !this.isParticipant;
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(FirebaseConversation.prototype, "initialized", {
        get: function () {
            return this._initialized;
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(FirebaseConversation.prototype, "metadata", {
        get: function () {
            return Object.assign({}, this._metadata);
        },
        enumerable: true,
        configurable: true
    });
    FirebaseConversation.prototype.handleNewMessage = function (snapshot) {
        var _this = this;
        snapshot.docChanges().forEach(function (change) {
            if (change.type === 'added') {
                _this.getMessageInternal(change.doc).then(function (message) {
                    if (!change.doc.metadata.hasPendingWrites) {
                        _this._eventEmitter.emit('newMessage', {
                            event: 'newMessage',
                            conversation: _this,
                            message: message
                        });
                    }
                    var modified = false;
                    if (!_this._lastMessage || _this._lastMessage.sentAt.isBefore(message.sentAt)) {
                        _this._lastMessage = message;
                        modified = true;
                    }
                    if (_this.isParticipant && message.sentBy.userId !== _this._me.userId) {
                        var readTimestamp = _this.getMyReadTimestamp();
                        if (!readTimestamp || readTimestamp.isBefore(message.sentAt)) {
                            _this._unreadCount = _this._unreadCount + 1;
                            modified = true;
                        }
                    }
                    if (modified) {
                        _this._eventEmitter.emit('changed', {
                            event: 'changed',
                            conversation: _this
                        });
                    }
                });
            }
            else if (change.type === 'modified') {
                var message = _this._messages.get(change.doc.id);
                if (message) {
                    message.updateMessage(change.doc);
                }
            }
        });
        var latestMessage = snapshot.docs.find(function (doc) { return !doc.metadata.hasPendingWrites; });
        if (latestMessage) {
            this._store
                .collection('conversations')
                .doc(this._conversationId)
                .collection('deliveredUntil')
                .doc(this._me.userId)
                .update('timestamp', latestMessage.get('sentAt'))
                .catch(function (err) {
                if (err.code === 'permission-denied') {
                    return;
                }
                throw err;
            });
        }
    };
    FirebaseConversation.prototype.init = function (conversation, typingTimestamps, deliveryTimestamps, readTimestamps) {
        var _this = this;
        if (!conversation.exists) {
            return;
        }
        if (conversation.id !== this._conversationId) {
            throw new Error("Expect conversation " + this._conversationId + ", but found " + conversation.id);
        }
        if (this._initialized) {
            return;
        }
        this._createdAt =
            fromFirebaseTimestamp(conversation.get('createdAt', { serverTimestamps: 'estimate' })) ||
                Timestamp.now();
        this._deliveryTimestamps = deliveryTimestamps;
        this._readTimestamps = readTimestamps;
        typingTimestamps.forEach(function (timestamp) { return _this.handleTyping(timestamp); });
        this.updateConversation(conversation);
    };
    FirebaseConversation.prototype.getMyReadTimestamp = function () {
        var _this = this;
        var readTimestamp = this._readTimestamps.find(function (readTimestamp) { return readTimestamp.id === _this._me.userId; });
        return readTimestamp && fromFirebaseTimestamp(readTimestamp.get('timestamp'));
    };
    FirebaseConversation.prototype.updateConversation = function (conversation) {
        var _this = this;
        var data = conversation.data({ serverTimestamps: 'estimate' });
        this._client.getUsers(Object.keys(data.participants)).then(function (participants) {
            _this._readOnly = data.readOnly;
            _this._updatedAt = fromFirebaseTimestamp(data.updatedAt) || Timestamp.now();
            _this._metadata = data.metadata;
            _this._participants = participants;
            _this._participantIds = new Set(participants.map(function (v) { return v.userId; }));
            var lastMessageId;
            if (!_this._initialized) {
                _this._unreadCount =
                    (data.participants[_this._me.userId] && data.participants[_this._me.userId].unreadCount) ||
                        0;
                lastMessageId = data.lastMessage && data.lastMessage.messageId;
            }
            else if (!_this.isParticipant) {
                _this._unreadCount = 0;
            }
            if (lastMessageId) {
                var lastMessage_1 = _this.getMessage(lastMessageId);
                if (lastMessage_1.initialized) {
                    _this._lastMessage = lastMessage_1;
                }
                else {
                    lastMessage_1.once('initialized', function () {
                        if (_this._lastMessage) {
                            return;
                        }
                        _this._lastMessage = lastMessage_1;
                        if (_this._initialized) {
                            _this._eventEmitter.emit('changed', {
                                event: 'changed',
                                conversation: _this
                            });
                        }
                    });
                }
            }
            if (!_this._initialized) {
                if (lastMessageId) {
                    _this._unsubscribeNewMessages = _this._store
                        .collection('conversations')
                        .doc(_this._conversationId)
                        .collection('messages')
                        .where('sentAt', '>', data.lastMessage.sentAt)
                        .orderBy('sentAt', 'desc')
                        .onSnapshot({ includeMetadataChanges: true }, _this.handleNewMessage, function (error) {
                        return console.error("Conversation " + _this._conversationId + " New Message Non-Empty Subscription Error (me=" + (_this._me && _this._me.userId) + "): ", error);
                    });
                }
                else {
                    _this._unsubscribeNewMessages = _this._store
                        .collection('conversations')
                        .doc(_this._conversationId)
                        .collection('messages')
                        .orderBy('sentAt', 'desc')
                        .onSnapshot({ includeMetadataChanges: true }, _this.handleNewMessage, function (error) {
                        return console.error("Conversation " + _this._conversationId + " New Message Subscription Empty Error (me=" + (_this._me && _this._me.userId) + "): ", error);
                    });
                }
            }
            if (_this._initialized) {
                _this._eventEmitter.emit('changed', {
                    event: 'changed',
                    conversation: _this
                });
            }
            else {
                _this._initialized = true;
                _this._eventEmitter.emit('initialized', {
                    event: 'initialized',
                    conversation: _this
                });
            }
        });
    };
    FirebaseConversation.prototype.doMarkConversationRead = function (upToMessageIds) {
        var _this = this;
        var readTimestamp = this._readTimestamps.find(function (readTimestamp) { return readTimestamp.id === _this._me.userId; });
        if (readTimestamp) {
            var latestSentAt_1 = upToMessageIds
                .map(function (messageId) { return _this.getMessage(messageId); })
                .filter(function (message) { return !message.pending && message.initialized; })
                .map(function (message) { return message.sentAt; })
                .reduce(function (prev, current) { return (prev.isBefore(current) ? current : prev); }, Timestamp.EPOCH);
            var readUntil = fromFirebaseTimestamp(readTimestamp.get('timestamp'));
            if (!readUntil || readUntil.isBefore(latestSentAt_1)) {
                readTimestamp.ref.update('timestamp', toFirebaseTimestamp(latestSentAt_1)).catch(function (err) {
                    if (err.code === 'permission-denied') {
                        return;
                    }
                    throw err;
                });
                this._unreadCount = 0;
                this._messages.forEach(function (message) {
                    if (message.initialized &&
                        message.sentBy.userId !== _this._me.userId &&
                        message.sentAt.isAfter(latestSentAt_1)) {
                        _this._unreadCount = _this._unreadCount + 1;
                    }
                    if (_this._initialized) {
                        _this._eventEmitter.emit('changed', {
                            event: 'changed',
                            conversation: _this
                        });
                    }
                });
            }
        }
    };
    FirebaseConversation.prototype.markConversationRead = function (upToMessageId) {
        var _this = this;
        if (!this.isParticipant) {
            return;
        }
        if (!upToMessageId) {
            var latestSentAt_2 = Timestamp.EPOCH;
            this._messages.forEach(function (message) {
                if (!message.pending && message.initialized && latestSentAt_2.isBefore(message.sentAt)) {
                    latestSentAt_2 = message.sentAt;
                    upToMessageId = message.messageId;
                }
            });
        }
        if (upToMessageId) {
            this._messageIdsToBeMarkedAsRead.push(upToMessageId);
            if (!this._markConversationAsReadTimeout) {
                this._markConversationAsReadTimeout = setTimeout(function () {
                    _this._markConversationAsReadTimeout = undefined;
                    _this.doMarkConversationRead(_this._messageIdsToBeMarkedAsRead);
                    _this._messageIdsToBeMarkedAsRead = [];
                }, 1000);
            }
        }
    };
    FirebaseConversation.prototype.markConversationDelivered = function (upToMessageId) {
        var _this = this;
        if (!this.isParticipant) {
            return;
        }
        var deliveryTimestamp = this._deliveryTimestamps.find(function (deliveryTimestamp) { return deliveryTimestamp.id === _this._me.userId; });
        if (deliveryTimestamp) {
            var deliveredUntil = fromFirebaseTimestamp(deliveryTimestamp.get('timestamp'));
            var upToMessage = this.getMessage(upToMessageId);
            if (!upToMessage.pending &&
                upToMessage.initialized &&
                (!deliveredUntil || deliveredUntil.isBefore(upToMessage.sentAt))) {
                deliveryTimestamp.ref
                    .update('timestamp', toFirebaseTimestamp(upToMessage.sentAt))
                    .catch(function (err) {
                    if (err.code === 'permission-denied') {
                        return;
                    }
                    throw err;
                });
            }
        }
    };
    FirebaseConversation.prototype.handleTypingUntilChange = function (change) {
        switch (change.type) {
            case 'added':
            case 'modified': {
                this.handleTyping(change.doc);
                break;
            }
            case 'removed': {
                if (this._typingTimeouts.has(change.doc.id)) {
                    clearTimeout(this._typingTimeouts.get(change.doc.id));
                    this._typingTimeouts.delete(change.doc.id);
                    this.fireTyping(change.doc.id, false);
                }
                break;
            }
        }
    };
    FirebaseConversation.prototype.modifyTimestampArray = function (change, array) {
        var index = array.findIndex(function (value) { return value.id === change.doc.id; });
        switch (change.type) {
            case 'added':
            case 'modified': {
                if (index === -1) {
                    array.push(change.doc);
                }
                else {
                    array[index] = change.doc;
                }
                break;
            }
            case 'removed': {
                if (index !== -1) {
                    array.splice(index, 1);
                }
                break;
            }
        }
        return array;
    };
    FirebaseConversation.prototype.handleDeliveredUntilChange = function (change) {
        this._deliveryTimestamps = this.modifyTimestampArray(change, this._deliveryTimestamps);
        this._messages.forEach(function (message) { return message.initialized && message.handleDeliveredUntilChange(change); });
    };
    FirebaseConversation.prototype.handleReadUntilChange = function (change) {
        this._readTimestamps = this.modifyTimestampArray(change, this._readTimestamps);
        this._messages.forEach(function (message) { return message.initialized && message.handleReadUntilChange(change); });
    };
    FirebaseConversation.prototype.startTyping = function (expiration) {
        if (!this.isParticipant) {
            return;
        }
        expiration = expiration || 5000;
        var typingUntil = typeof expiration === 'number' ? Timestamp.fromMillis(Date.now() + expiration) : expiration;
        this._store
            .collection('conversations')
            .doc(this._conversationId)
            .collection('typingUntil')
            .doc(this._me.userId)
            .set({
            timestamp: toFirebaseTimestamp(typingUntil),
            userId: this._me.userId,
            conversationId: this._conversationId
        });
    };
    Object.defineProperty(FirebaseConversation.prototype, "myDeliveryTimestamp", {
        get: function () {
            var _this = this;
            var deliveryTimestamp = this._deliveryTimestamps.find(function (deliveryTimestamp) { return deliveryTimestamp.id === _this._me.userId; });
            return deliveryTimestamp && fromFirebaseTimestamp(deliveryTimestamp.get('timestamp'));
        },
        enumerable: true,
        configurable: true
    });
    FirebaseConversation.prototype.close = function () {
        _super.prototype.close.call(this);
        if (this._markConversationAsReadTimeout) {
            clearTimeout(this._markConversationAsReadTimeout);
        }
        if (this._messageIdsToBeMarkedAsRead.length > 0) {
            this.doMarkConversationRead(this._messageIdsToBeMarkedAsRead);
            this._messageIdsToBeMarkedAsRead = [];
        }
        if (this._unsubscribeNewMessages) {
            this._unsubscribeNewMessages();
            this._unsubscribeNewMessages = undefined;
        }
        this._deliveryTimestamps = [];
        this._readTimestamps = [];
        this._typingTimeouts.forEach(function (timeout) { return clearTimeout(timeout); });
        this._typingTimeouts.clear();
        this._eventEmitter.removeAllListeners();
        this._messages.forEach(function (message) { return message.close(); });
        this._messages.clear();
    };
    FirebaseConversation.prototype.stopTyping = function () {
        if (!this.isParticipant) {
            return;
        }
        this._store
            .collection('conversations')
            .doc(this._conversationId)
            .collection('typingUntil')
            .doc(this._me.userId)
            .delete();
    };
    FirebaseConversation.prototype.getMessageQuery = function (limit) {
        return new FirebaseMessageQuery(this._store, this, this._client, limit);
    };
    FirebaseConversation.prototype.getMessage = function (messageId) {
        var _this = this;
        if (this._messages.has(messageId)) {
            return this._messages.get(messageId);
        }
        else {
            var message_1 = new FirebaseMessage(this._client, this, messageId);
            this._messages.set(messageId, message_1);
            this._store
                .collection('conversations')
                .doc(this._conversationId)
                .collection('messages')
                .doc(messageId)
                .get()
                .then(function (snapshot) { return message_1.init(snapshot, _this._deliveryTimestamps, _this._readTimestamps); });
            return message_1;
        }
    };
    FirebaseConversation.prototype.getMessageInternal = function (firebaseMessage) {
        if (this._messages.has(firebaseMessage.id)) {
            var message_2 = this._messages.get(firebaseMessage.id);
            if (message_2.initialized) {
                return Promise.resolve(message_2);
            }
            return new Promise(function (resolve) { return message_2.once('initialized', function () { return resolve(message_2); }); });
        }
        else {
            var message_3 = new FirebaseMessage(this._client, this, firebaseMessage.id);
            var promise = new Promise(function (resolve) {
                return message_3.once('initialized', function () { return resolve(message_3); });
            });
            message_3.init(firebaseMessage, this._deliveryTimestamps, this._readTimestamps);
            this._messages.set(firebaseMessage.id, message_3);
            return promise;
        }
    };
    FirebaseConversation.prototype.createDraftMessage = function () {
        if (!this.isParticipant) {
            throw new Error("Not a participant (me=" + this._me.userId + ")");
        }
        return new FirebaseMessage(this._client, this, uuidv4()).initDraft();
    };
    FirebaseConversation.prototype.sendMessage = function (draftMessage) {
        if (!this.isParticipant) {
            throw new Error("Not a participant (me=" + this._me.userId + ")");
        }
        if (!draftMessage.draft) {
            throw new Error('Message to be sent must be a draft message.');
        }
        if (draftMessage.conversation !== this) {
            throw new Error("Message to be sent must be a draft message for this conversation (" + this._conversationId + "), but is for (" + draftMessage.conversation.conversationId + ").");
        }
        draftMessage.finalizeDraft();
        var messageId = draftMessage.messageId;
        var messageRef = this._store
            .collection('conversations')
            .doc(this._conversationId)
            .collection('messages')
            .doc(messageId);
        this._messages.set(messageId, draftMessage);
        messageRef.set({
            conversationId: this._conversationId,
            messageId: messageId,
            sentAt: toFirebaseTimestamp(draftMessage.sentAt),
            sentBy: draftMessage.sentBy.userId,
            realSenderId: this._client.realMyself.userId,
            parts: draftMessage.parts.map(function (part) { return part.toFirestoreData(); }),
            metadata: draftMessage.metadata
        });
        return draftMessage;
    };
    FirebaseConversation.prototype.handleTyping = function (typingTimestamp) {
        var _this = this;
        var userId = typingTimestamp.get('userId');
        if (userId === this._me.userId) {
            return;
        }
        var wasTyping = this._typingTimeouts.has(userId);
        var timeout = (fromFirebaseTimestamp(typingTimestamp.get('timestamp')) || Timestamp.EPOCH).toMillis();
        var now = Date.now();
        if (wasTyping) {
            clearTimeout(this._typingTimeouts.get(userId));
            this._typingTimeouts.delete(userId);
        }
        if (now < timeout) {
            if (!wasTyping) {
                this.fireTyping(userId, true);
            }
            this._typingTimeouts.set(userId, setTimeout(function () {
                _this._typingTimeouts.delete(userId);
                _this.fireTyping(userId, false);
            }, timeout - now));
        }
        else {
            if (wasTyping) {
                this.fireTyping(userId, false);
            }
        }
    };
    FirebaseConversation.prototype.on = function (event, callback, context) {
        var _this = this;
        if (event === 'typingStarted') {
            this._client.getUsers(Array.from(this._typingTimeouts.keys())).then(function (users) {
                return users.forEach(function (user) {
                    callback.call(context, {
                        event: event,
                        conversation: _this,
                        user: user
                    });
                });
            });
        }
        return _super.prototype.on.call(this, event, callback, context);
    };
    FirebaseConversation.prototype.fireTyping = function (userId, typing) {
        var _this = this;
        this._client.getUsers([userId]).then(function (_a) {
            var user = _a[0];
            var eventName = typing ? 'typingStarted' : 'typingStopped';
            _this._eventEmitter.emit(eventName, {
                event: eventName,
                conversation: _this,
                user: user
            });
        });
    };
    return FirebaseConversation;
}(BaseModel));

var FirebaseUser = (function () {
    function FirebaseUser(client, userId, displayName, internalMessagingUser, type, avatarUrl, expertRating, vanityId) {
        this._client = client;
        this._userId = userId;
        this._vanityId = vanityId;
        this._displayName = displayName;
        this._internalMessagingUser = internalMessagingUser;
        this._type = type;
        this._avatarUrl = avatarUrl;
        this._expertRating = expertRating;
    }
    Object.defineProperty(FirebaseUser.prototype, "userId", {
        get: function () {
            return this._userId;
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(FirebaseUser.prototype, "displayName", {
        get: function () {
            return this._displayName;
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(FirebaseUser.prototype, "avatarUrl", {
        get: function () {
            return this._avatarUrl;
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(FirebaseUser.prototype, "isMyself", {
        get: function () {
            return this._client.myself && this._userId === this._client.myself._userId;
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(FirebaseUser.prototype, "isInternalMessagingUser", {
        get: function () {
            return this._internalMessagingUser;
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(FirebaseUser.prototype, "type", {
        get: function () {
            return this._type;
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(FirebaseUser.prototype, "expertRating", {
        get: function () {
            return this._expertRating;
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(FirebaseUser.prototype, "vanityId", {
        get: function () {
            return this._vanityId;
        },
        enumerable: true,
        configurable: true
    });
    return FirebaseUser;
}());

var FirebaseChatClient = (function (_super) {
    __extends(FirebaseChatClient, _super);
    function FirebaseChatClient(app, fetchUserProfiles) {
        var _this = _super.call(this) || this;
        _this._auth = app.auth();
        _this._store = app.firestore();
        _this._store.settings({});
        _this._conversations = new Map();
        _this._users = new Map();
        _this._unsubscribes = [];
        _this._unreadMessageCount = 0;
        _this._unreadConversations = new Map();
        _this._fetchUserProfiles = fetchUserProfiles;
        _this.login = _this.login.bind(_this);
        _this.logout = _this.logout.bind(_this);
        _this.getConversation = _this.getConversation.bind(_this);
        _this.close = _this.close.bind(_this);
        _this.getUsers = _this.getUsers.bind(_this);
        _this.assumeIdentity = _this.assumeIdentity.bind(_this);
        _this.handleConversationChange = _this.handleConversationChange.bind(_this);
        return _this;
    }
    Object.defineProperty(FirebaseChatClient.prototype, "myself", {
        get: function () {
            return this._me;
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(FirebaseChatClient.prototype, "realMyself", {
        get: function () {
            return this._realMe;
        },
        enumerable: true,
        configurable: true
    });
    FirebaseChatClient.prototype.getUsers = function (userIds) {
        return this._getUsers(userIds);
    };
    FirebaseChatClient.prototype.recalculateUnreadMessageCount = function () {
        var _this = this;
        this._unreadMessageCount = 0;
        this._unreadConversations.forEach(function (unreadCount) { return (_this._unreadMessageCount += unreadCount); });
        this._eventEmitter.emit('unreadChanged', {
            event: 'unreadChanged',
            client: this
        });
    };
    FirebaseChatClient.prototype._getUsers = function (userIds) {
        var _this = this;
        var existingUsers = userIds
            .map(function (userId) { return _this._users.get(userId); })
            .filter(function (user) { return !!user; })
            .map(function (user) { return user; });
        var fetchingUserIds = userIds.filter(function (userId) { return !_this._users.has(userId); });
        if (fetchingUserIds.length === 0) {
            return Promise.resolve(existingUsers);
        }
        return this._fetchUserProfiles(fetchingUserIds).then(function (fetchedProfiles) {
            var fetchedUsers = fetchedProfiles.map(function (fetchedProfile) {
                var user = new FirebaseUser(_this, fetchedProfile.userId, fetchedProfile.displayName, fetchedProfile.internalMessagingUser, fetchedProfile.type, fetchedProfile.avatarUrl, fetchedProfile.expertRating, fetchedProfile.vanityId);
                _this._users.set(user.userId, user);
                return user;
            });
            return fetchedUsers.concat(existingUsers);
        });
    };
    FirebaseChatClient.prototype.login = function (token) {
        var _this = this;
        if (this._auth.currentUser) {
            return this.logout().then(function () { return _this.login(token); });
        }
        return this._auth.setPersistence(firebase.auth.Auth.Persistence.NONE).then(function () {
            return _this._auth.signInWithCustomToken(token).then(function (credential) {
                if (credential.user) {
                    var uid_1 = credential.user.uid;
                    return _this._getUsers([uid_1]).then(function (_a) {
                        var me = _a[0];
                        _this._me = me;
                        _this._realMe = me;
                        _this._unsubscribes.push(_this._store
                            .collection('conversations')
                            .where("participants." + uid_1 + ".unreadCount", '>', 0)
                            .onSnapshot(function (snapshots) {
                            snapshots.docChanges().forEach(function (change) {
                                if (!_this._conversations.has(change.doc.id)) {
                                    switch (change.type) {
                                        case 'added':
                                        case 'modified':
                                            _this._unreadConversations.set(change.doc.id, change.doc.get("participants." + uid_1 + ".unreadCount"));
                                            break;
                                        case 'removed':
                                            _this._unreadConversations.delete(change.doc.id);
                                            break;
                                    }
                                }
                            });
                            _this.recalculateUnreadMessageCount();
                        }, function (error) {
                            return console.error("Global Per-Conversation Unread Count Subscription Error (me=" + (_this._me &&
                                _this._me.userId) + "): ", error);
                        }), _this._store
                            .collection('messages')
                            .where('sentBy', '==', uid_1)
                            .where('sentAt', '>', firebase.firestore.Timestamp.now())
                            .orderBy('sentAt', 'desc')
                            .onSnapshot(function (snapshot) {
                            snapshot
                                .docChanges()
                                .filter(function (change) { return change.type === 'added'; })
                                .forEach(function (change) {
                                return _this.fireNewMessage(change.doc.get('conversationId'), change.doc.id);
                            });
                        }, function (error) {
                            return console.error("Global New Sent Message Subscription Error (me=" + (_this._me &&
                                _this._me.userId) + "): ", error);
                        }), _this._store
                            .collection('messages')
                            .where("recipients." + uid_1, '>', firebase.firestore.Timestamp.now())
                            .onSnapshot(function (snapshot) {
                            snapshot
                                .docChanges()
                                .filter(function (change) { return change.type === 'added'; })
                                .forEach(function (change) {
                                return _this.fireNewMessage(change.doc.get('conversationId'), change.doc.id);
                            });
                        }, function (error) {
                            return console.error("Global New Received Message Subscription Error (me=" + (_this._me &&
                                _this._me.userId) + "): ", error);
                        }));
                        return uid_1;
                    });
                }
                _this._me = undefined;
                throw new Error('User ID not available.');
            });
        });
    };
    FirebaseChatClient.prototype.assumeIdentity = function (userId) {
        var _this = this;
        if (!this._auth.currentUser) {
            return Promise.reject('Not logged in');
        }
        if (!userId || userId === this._realMe.userId) {
            this._me = this._realMe;
            return Promise.resolve();
        }
        return this._auth.currentUser.getIdTokenResult().then(function (token) {
            if (!token.claims.allowedIdentities[userId]) {
                throw "Assuming identity for " + userId + " is not allowed.";
            }
            return _this._getUsers([userId]).then(function (assumedUser) {
                _this._me = assumedUser[0];
                return;
            });
        });
    };
    FirebaseChatClient.prototype.fireNewMessage = function (conversationId, messageId) {
        var _this = this;
        var conversation = this.getConversation(conversationId);
        var getNewMessage = function () {
            var message = conversation.getMessage(messageId);
            var fireEvent = function () {
                _this._eventEmitter.emit('newMessage', {
                    event: 'newMessage',
                    client: _this,
                    message: message
                });
            };
            if (message.initialized) {
                fireEvent();
            }
            else {
                message.once('initialized', fireEvent);
            }
        };
        if (conversation.initialized) {
            getNewMessage();
        }
        else {
            conversation.once('initialized', getNewMessage);
        }
    };
    Object.defineProperty(FirebaseChatClient.prototype, "unreadMessageCount", {
        get: function () {
            return this._unreadMessageCount;
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(FirebaseChatClient.prototype, "unreadConversationCount", {
        get: function () {
            return this._unreadConversations.size;
        },
        enumerable: true,
        configurable: true
    });
    FirebaseChatClient.prototype.tryInitializeConversation = function (conversation, initData) {
        if (initData.conversation &&
            initData.deliveredUntil &&
            initData.readUntil &&
            initData.typingUntil) {
            conversation.init(initData.conversation, initData.typingUntil, initData.deliveredUntil, initData.readUntil);
            return true;
        }
        return false;
    };
    FirebaseChatClient.prototype.handleConversationChange = function (event) {
        if (event.conversation.unreadCount === 0) {
            this._unreadConversations.delete(event.conversation.conversationId);
        }
        else {
            this._unreadConversations.set(event.conversation.conversationId, event.conversation.unreadCount);
        }
        this.recalculateUnreadMessageCount();
    };
    FirebaseChatClient.prototype.getConversation = function (conversationId) {
        var _this = this;
        if (!this._me) {
            throw new Error('Not authenticated');
        }
        if (this._conversations.has(conversationId)) {
            return this._conversations.get(conversationId);
        }
        else {
            var conversation_1 = new FirebaseConversation(this._store, this, conversationId);
            var initDataHolder_1 = { data: {} };
            this._conversations.set(conversationId, conversation_1);
            conversation_1.once('initialized', this.handleConversationChange);
            conversation_1.on('changed', this.handleConversationChange);
            this._unsubscribes.push(this._store
                .collection('conversations')
                .doc(conversationId)
                .onSnapshot(function (snapshot) {
                if (!conversation_1.initialized) {
                    initDataHolder_1.data.conversation = snapshot;
                    if (_this.tryInitializeConversation(conversation_1, initDataHolder_1.data)) {
                        initDataHolder_1.data = {};
                    }
                }
                else {
                    conversation_1.updateConversation(snapshot);
                }
            }, function (error) {
                return console.error("Conversation " + conversationId + " Update Subscription Error (me=" + (_this._me &&
                    _this._me.userId) + "): ", error);
            }), this._store
                .collection('conversations')
                .doc(conversationId)
                .collection('typingUntil')
                .onSnapshot(function (snapshot) {
                if (!conversation_1.initialized) {
                    initDataHolder_1.data.typingUntil = snapshot.docs;
                    if (_this.tryInitializeConversation(conversation_1, initDataHolder_1.data)) {
                        initDataHolder_1.data = {};
                    }
                }
                else {
                    snapshot
                        .docChanges()
                        .forEach(function (change) { return conversation_1.handleTypingUntilChange(change); });
                }
            }, function (error) {
                return console.error("Conversation " + conversationId + " Typing Indicator Subscription Error (me=" + (_this
                    ._me && _this._me.userId) + "): ", error);
            }), this._store
                .collection('conversations')
                .doc(conversationId)
                .collection('deliveredUntil')
                .onSnapshot(function (snapshot) {
                if (!conversation_1.initialized) {
                    initDataHolder_1.data.deliveredUntil = snapshot.docs;
                    if (_this.tryInitializeConversation(conversation_1, initDataHolder_1.data)) {
                        initDataHolder_1.data = {};
                    }
                }
                else {
                    snapshot
                        .docChanges()
                        .forEach(function (change) { return conversation_1.handleDeliveredUntilChange(change); });
                }
            }, function (error) {
                return console.error("Conversation " + conversationId + " Delivery Receipt Subscription Error (me=" + (_this
                    ._me && _this._me.userId) + "): ", error);
            }), this._store
                .collection('conversations')
                .doc(conversationId)
                .collection('readUntil')
                .onSnapshot(function (snapshot) {
                if (!conversation_1.initialized) {
                    initDataHolder_1.data.readUntil = snapshot.docs;
                    if (_this.tryInitializeConversation(conversation_1, initDataHolder_1.data)) {
                        initDataHolder_1.data = {};
                    }
                }
                else {
                    snapshot.docChanges().forEach(function (change) { return conversation_1.handleReadUntilChange(change); });
                }
            }, function (error) {
                return console.error("Conversation " + conversationId + " Read Receipt Subscription Error (me=" + (_this
                    ._me && _this._me.userId) + "): ", error);
            }));
            return conversation_1;
        }
    };
    FirebaseChatClient.prototype.logout = function () {
        var _this = this;
        if (this._auth.currentUser) {
            this._conversations.forEach(function (conversation) { return conversation.close(); });
            this._conversations.clear();
            this._users.clear();
            this._unsubscribes.forEach(function (unsubscribe) { return unsubscribe(); });
            this._unsubscribes = [];
            return this._auth.signOut().then(function () {
                _this._me = undefined;
            });
        }
        else {
            return Promise.resolve();
        }
    };
    FirebaseChatClient.prototype.close = function () {
        _super.prototype.close.call(this);
        this.logout();
    };
    return FirebaseChatClient;
}(BaseModel));

function createClient(options) {
    if (firebase.apps.filter(function (app) { return app && app.name === options.projectId; }).length) {
        return new FirebaseChatClient(firebase.app(options.projectId), options.fetchUserProfiles);
    }
    else {
        return new FirebaseChatClient(firebase.initializeApp({
            apiKey: options.apiKey,
            authDomain: options.projectId + ".firebaseapp.com",
            projectId: options.projectId
        }, options.projectId), options.fetchUserProfiles);
    }
}

export { FirebaseConversation, FirebaseMessage, FirebaseMessagePart, FirebaseMessageChannel, FirebaseMessageReceiptType, FirebaseMessageQuery, FirebaseUser, FirebaseChatClient, createClient };
//# sourceMappingURL=index.esm.js.map
